import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { Heatmap, HeatmapKind, SnapshotEvent } from "@backstory/protocol";
import { api } from "../../../api";
import { useAuth } from "../../../auth/store";
import { ErrorState, Skeleton } from "../../../components/States";
import { PageHeader, SearchSelect, type SearchSelectOption } from "../../../components/ui";
import { insightsApi, insightsKeys, type HeatmapRoute } from "../api";
import { useSlowPending, WakingNumbers } from "../WakingNumbers";
import { RangePicker, useRange } from "../components";
import { HeatmapStage } from "../heatmap/HeatmapStage";

const KINDS: Array<{ id: HeatmapKind; label: string }> = [
  { id: "click", label: "Clicks" },
  { id: "move", label: "Moves" },
  { id: "scroll", label: "Scroll depth" },
  { id: "rage", label: "Rage clicks" },
  { id: "dead", label: "Dead clicks" },
];
const BREAKPOINTS: Array<{ id: Heatmap["breakpoint"]; label: string }> = [
  { id: "desktop", label: "Desktop" },
  { id: "tablet", label: "Tablet" },
  { id: "mobile", label: "Mobile" },
];

/** Routes for the picker. The current route stays selectable when this period has no count for it. */
export function routeOptions(routes: HeatmapRoute[], current: string): SearchSelectOption[] {
  const rows = current && !routes.some((r) => r.route === current) ? [{ route: current, sessions: -1 }, ...routes] : routes;
  return rows.map((r) => ({
    value: r.route,
    label: r.route,
    detail: r.sessions >= 0 ? `${r.sessions.toLocaleString()} ${r.sessions === 1 ? "session" : "sessions"}` : undefined,
  }));
}

/** Fetch the representative snapshot: the chunk holding the referenced checkpoint, then its snapshot event. */
export async function loadSnapshot(hm: Heatmap): Promise<SnapshotEvent | null> {
  if (!hm.snapshotSessionId) return null;
  const detail = await api.session(hm.snapshotSessionId);
  const ref = detail.chunks.find((c) => hm.snapshotCheckpointId === undefined || c.checkpointId === hm.snapshotCheckpointId) ?? detail.chunks[0];
  if (!ref) return null;
  const chunk = await api.chunk(hm.snapshotSessionId, ref.seq);
  const snap = chunk.events.find((e): e is SnapshotEvent => e.type === "snapshot" && (hm.snapshotCheckpointId === undefined || e.checkpointId === hm.snapshotCheckpointId)) ?? chunk.events.find((e): e is SnapshotEvent => e.type === "snapshot");
  return snap ?? null;
}

export function HeatmapsPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const { range } = useRange();
  const route = typeof search.route === "string" ? search.route : "";
  const kind = (KINDS.some((k) => k.id === search.kind) ? search.kind : "click") as HeatmapKind;
  const breakpoint = (BREAKPOINTS.some((b) => b.id === search.bp) ? search.bp : "desktop") as Heatmap["breakpoint"];
  const setSearch = (patch: Record<string, unknown>) => {
    const next: Record<string, unknown> = { ...search, ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === "") delete next[k];
    void navigate({ to: "/heatmaps", search: next as never, replace: true });
  };

  // Routes that carry heat at this breakpoint, rather than the dashboard's
  // intentionally short top-ten list of traffic.
  const routeq = useQuery({
    queryKey: insightsKeys.heatmapRoutes(range, breakpoint, auth.currentProjectId),
    queryFn: () => insightsApi.heatmapRoutes(range, breakpoint),
    retry: 0,
  });
  // An API older than this dashboard (a self-hosted upgrade mid-flight) has no
  // route catalog. Top routes still give the picker something to offer.
  const fallbackq = useQuery({
    queryKey: insightsKeys.overview(range, auth.currentProjectId),
    queryFn: () => insightsApi.overview(range),
    enabled: routeq.isError,
    retry: 0,
  });
  const routes = routeq.data ?? fallbackq.data?.topRoutes ?? [];
  const options = useMemo(() => routeOptions(routes, route), [routes, route]);
  const screenLabel = BREAKPOINTS.find((b) => b.id === breakpoint)?.label.toLowerCase() ?? breakpoint;
  useEffect(() => {
    if (!route && routes[0]) setSearch({ route: routes[0].route });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes.length]);

  const hmq = useQuery({
    queryKey: insightsKeys.heatmap({ route, kind, breakpoint, ...range }, auth.currentProjectId),
    queryFn: () => insightsApi.heatmap({ route, kind, breakpoint, ...range }),
    enabled: !!route,
  });
  const snapq = useQuery({
    queryKey: ["insights", "heatmapSnapshot", hmq.data?.snapshotSessionId, hmq.data?.snapshotCheckpointId],
    queryFn: () => loadSnapshot(hmq.data!),
    enabled: !!hmq.data?.snapshotSessionId,
    retry: 0,
  });

  const waitingForRoutes = !route && (routeq.isPending || fallbackq.isFetching);
  const waitingForMap = !!route && hmq.isPending;
  const slowLoad = useSlowPending(waitingForRoutes || waitingForMap);
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageW, setStageW] = useState(900);
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const update = () => {
      // Matches .hm-layout: a 240px sidebar plus the grid gap, above the
      // breakpoint where the sidebar drops below the stage.
      const sideBySide = window.matchMedia?.("(min-width: 1025px)").matches ?? el.clientWidth > 1024;
      setStageW(Math.max(280, el.clientWidth - (sideBySide ? 256 : 0)));
    };
    update();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      <PageHeader title="Heatmaps" actions={<RangePicker />}>
        <div className="ins-filters hm-filters" role="search" aria-label="Heatmap filters">
          <label>
            Route
            <SearchSelect
              className="hm-route-select"
              menuClassName="hm-route-menu"
              aria-label="Route"
              value={route}
              options={options}
              placeholder="Choose a route"
              searchPlaceholder="Search routes"
              empty="No routes in this period."
              disabled={routeq.isPending && options.length === 0}
              onChange={(next) => setSearch({ route: next })}
            />
          </label>
          <div className="hm-toggle" role="group" aria-label="Heatmap kind">
            {KINDS.map((k) => (<button key={k.id} type="button" className="btn" aria-pressed={kind === k.id} onClick={() => setSearch({ kind: k.id })}>{k.label}</button>))}
          </div>
          <div className="hm-toggle" role="group" aria-label="Breakpoint">
            {BREAKPOINTS.map((b) => (<button key={b.id} type="button" className="btn" aria-pressed={breakpoint === b.id} onClick={() => setSearch({ bp: b.id })}>{b.label}</button>))}
          </div>
        </div>
      </PageHeader>
      <div ref={stageRef}>
        {!route ? (
          waitingForRoutes ? (
            slowLoad ? <WakingNumbers /> : <Skeleton lines={12} />
          ) : (
            <div className="card section muted" role="status">
              {routes.length === 0 ? `No ${screenLabel} sessions in this period. Try another period or screen size.` : "Choose a route."}
            </div>
          )
        ) : hmq.isPending ? (
          slowLoad ? <WakingNumbers /> : <Skeleton lines={12} />
        ) : hmq.isError ? (
          <div className="card section"><ErrorState error={hmq.error} retry={() => void hmq.refetch()} /></div>
        ) : (
          <HeatmapStage
            heatmap={hmq.data}
            snapshot={snapq.data ?? null}
            snapshotLoading={snapq.isFetching}
            snapshotError={snapq.isError}
            containerWidth={stageW}
          />
        )}
      </div>
    </>
  );
}
